import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Bot, Send, Loader2, X, Volume2, VolumeX, Sparkles, Smile,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUGGESTED = [
  "What can AllBaise do?",
  "How do I transcribe audio?",
  "Explain the African Languages translation",
  "How does SmartPay work?",
  "What is the AI Assistant?",
  "Tell me about AllBaise features",
];

export default function AllBaisePal() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const speakText = (text) => {
    if (!voiceEnabled) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text.replace(/[*#_`\[\]]/g, ""));
    utterance.rate = 1.1;
    utterance.pitch = 1.0;
    utteranceRef.current = utterance;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const sendMessage = async (text) => {
    const message = text || input.trim();
    if (!message || isLoading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: message }]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/allbaise/ask`, {
        question: message,
        history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true,
      });

      const reply = res.data.answer || res.data.response || "I'm not sure how to answer that.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);

      // Auto-speak the response if voice is on
      if (voiceEnabled) {
        setTimeout(() => speakText(reply), 300);
      }
    } catch (err) {
      toast.error("AllBaise Pal is thinking too hard. Try again.");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I'm having trouble connecting to my knowledge base. Please try your question again.",
      }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleVoiceToggle = () => {
    if (voiceEnabled) {
      stopSpeaking();
      setVoiceEnabled(false);
    } else {
      setVoiceEnabled(true);
      // Speak the last assistant message if any
      const lastMsg = [...messages].reverse().find(m => m.role === "assistant");
      if (lastMsg) speakText(lastMsg.content);
    }
  };

  return (
    <>
      {/* Floating face icon button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 hover:shadow-xl active:scale-95"
        title="Ask AllBaise Pal"
      >
        <div className="relative">
          <Bot className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
        </div>
      </button>

      {/* Chat dialog */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col animate-in slide-in-from-bottom-5 fade-in duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Smile className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-white text-sm">AllBaise Pal</span>
                  <Sparkles className="w-3 h-3 text-amber-200" />
                </div>
                <p className="text-[10px] text-white/70">Your AI guide to AllBaise</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleVoiceToggle}
                className={`p-1.5 rounded-md transition-colors ${
                  voiceEnabled ? "bg-white/20 text-white" : "text-white/60 hover:text-white hover:bg-white/10"
                }`}
                title={voiceEnabled ? "Mute voice" : "Enable voice"}
              >
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea ref={scrollRef} className="flex-1 p-4 bg-slate-50/50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mb-4">
                  <Bot className="w-8 h-8 text-amber-500" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">
                  Hi! I'm AllBaise Pal
                </p>
                <p className="text-xs text-slate-500 mb-4 max-w-64">
                  I can answer your questions about AllBaise — how features work, what each tool does, and more.
                </p>
                <div className="grid grid-cols-1 gap-1.5 w-full max-w-64">
                  {SUGGESTED.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="text-xs text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/50 transition-colors text-slate-600 hover:text-slate-800"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      m.role === "user"
                        ? "bg-amber-500 text-white rounded-tr-md"
                        : "bg-white border border-slate-200 rounded-tl-md text-slate-700"
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3">
                      <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about AllBaise..."
                className="flex-1 text-sm border border-slate-300 rounded-xl px-3 py-2 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 placeholder:text-slate-400"
                disabled={isLoading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={isLoading || !input.trim()}
                className="p-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              Powered by Qwen 3.5 — Knowledgeable about all AllBaise features
            </p>
          </div>
        </div>
      )}
    </>
  );
}
